<template>
  <div>
    <Toolbar
      v-if="flatCategories.length > 0"
      ref="toolbar"
      :toolbarItems="flatCategories"
      :selected-item-id="selectedCategoryId"
      @item-click="onToolbarCategoryClick"
      class="sticky-toolbar"
    />
    <FlexLayout role="region">
      <div class="product-section">
        <vue-loading :active="isLoading" :is-full-page="true" />
        <div id="menuCategories">
          <div v-if="!isLoading && !isInitialLoad && selectedCategory" class="meal-planner-columns">
            <!-- MealPlan Column -->
            <div class="mealplan-column">
              <div>
                <OrderMenu v-if="selectedCategory && Array.isArray(selectedCategory.products)"
                           :category="selectedCategory"
                           :basketTile="basketTile"
                           ref="mealPlanItem"
                           @basket-tile-updated="onBasketTileUpdated"
                           @product-quantity-change="handleProductQuantityChange"
                />
                <div v-else-if="!categories" class="meal-plan-noOrderAvailable">
                  <h3>{{$t('orderMenu.label.noCategories')}}</h3>
                </div>
              </div>
              <GoToBasketButton
                class="d-md-none"
                v-if="basketTile && (basketTile.numberOfItems > 0 || foodPackagingProducts.length > 0)"
                :product-count="basketTile ? basketTile.numberOfItems : 0"
                :formatted-total="basketTile ? basketTile.formattedTotal : 0.00"
                :hidden="!basketTile || (basketTile.numberOfItems === 0 && foodPackagingProducts.length === 0)"
                v-collapse:100="!basketTile || (basketTile.numberOfItems === 0 && foodPackagingProducts.length === 0)"
                @open-basket-dialog="showBasketDialog = true"
              />
            </div>
          </div>
          <transition name="fade-up">
            <div v-if="showBasketDialog" class="basket-dialog-overlay basket-dialog-mobile" @click.self="showBasketDialog = false">
              <div class="basket-dialog-content basket-dialog-content-mobile">
                <div class="basket-dialog-mobile__header">
                  <Button
                    class="basket-dialog-mobile__close"
                    secondary
                    preset-size
                    icon-left="close"
                    :icon-only="true"
                    @click="showBasketDialog = false"
                  >
                    <template #srText>{{ $t('system.button.close') }}</template>
                  </Button>
                </div>
                <Basket
                  :basketTile="basketTile"
                  :food-packaging-products="foodPackagingProducts"
                  :food-packaging-mandatory-error="showFoodPackagingValidationError"
                  :show-vat="false"
                  @product-quantity-change="handleProductQuantityChange"
                  @food-packaging-select="handleFoodPackagingProductSelect"
                />
                <div>
                  <Button icon-left="money-bill" @click="goToCheckout" :disabled="!basketTile || basketTile.numberOfItems === 0 || isFoodPackagingSelectionUpdating">
                    {{ $t('orderMenu.button.checkout') }}
                  </Button>
                  <Button icon-left="close" secondary @click="showBasketDialog = false">
                    {{ $t('system.button.close') }}
                  </Button>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>
      <div class="basket-section checkout-right d-none d-md-block">
        <BlockContainer variant="wide" :title="$t('orderMenu.subTitle.basket')">
          <Basket
            :basketTile="basketTile"
            :food-packaging-products="foodPackagingProducts"
            :food-packaging-mandatory-error="showFoodPackagingValidationError"
            :show-vat="false"
            @product-quantity-change="handleProductQuantityChange"
            @food-packaging-select="handleFoodPackagingProductSelect"
          />
          <template v-slot:actions>
            <Button icon-left="money-bill" @click="goToCheckout" :disabled="!basketTile || basketTile.numberOfItems === 0 || isFoodPackagingSelectionUpdating">
              {{ $t('orderMenu.button.checkout') }}
            </Button>
          </template>
        </BlockContainer>
      </div>
    </FlexLayout>
  </div>
</template>

<script>
import Toolbar from "@/components/navigation/Toolbar.vue";
import { productService } from "@/services/api/productService";
import GoToBasketButton from "@/components/sub-components/GoToBasketButton.vue";
import {basketService} from "@/services/api/basketService";
import {useShopStore} from "@/stores/shop";
import {useAppStore} from "@/stores/app";
import basketMixin from "@/components/mixins/basketMixin";
import Basket from "@/components/sub-components/Basket/Basket.vue";
import VueLoading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import Button from "@/components/actions/Button.vue";
import OrderMenu from "@/components/page-components/Order/OrderMenu.vue";
import productsMixin from "@/components/mixins/productsMixin";
import FlexLayout from "@/components/layout-components/Main/FlexLayout.vue";
import BlockContainer from "@/components/layout/BlockContainer.vue";
import { mapFoodPackagingProducts } from "@/utils/productUtils";
import { Order } from "@/constants/constants";
import { getCanonicalOrderType } from "@/utils/orderUtils";

export default {
  mixins: [
    basketMixin,
    productsMixin
  ],
  components: {
    BlockContainer,
    FlexLayout,
    Button,
    Basket,
    Toolbar,
    OrderMenu,
    GoToBasketButton,
    VueLoading,
  },
  inject: ["setPageMetaTitle"],
  data() {
    return {
      selectedDate: this.appStore.getSelectedMealPlanDate
        ? new Date(this.appStore.getSelectedMealPlanDate)
        : new Date(),
      selectedCategory: null,
      selectedCategoryId: null,
      flatCategories: [],
      categories: [],
      isLoading: false,
      basketTile: null,
      foodPackagingProducts: [],
      isFoodPackagingSelectionUpdating: false,
      showFoodPackagingValidationError: false,
      showBasketDialog: false,
      isInitialLoad: true,
      selectedChildId: null,
    };
  },
  setup() {
    const shopStore = useShopStore();
    const appStore = useAppStore();

    return {
      shopStore,
      appStore,
    };
  },
  updated(){
    Array.from(document.querySelectorAll(".stickyZero"))
      .forEach(function(e) { // give CSS the value. It does not have that.
        e.style.top = e.parentElement.offsetTop + "px"; // important with offsetTop, the element (e) can not have a margin-top!
      });
  },
  async created() {
    await this.setPageMetaTitle(this.shopStore.name);

    if (window.config.LAZY_LOAD_LIMIT !== undefined && window.config.LAZY_LOAD_LIMIT > 0) {
      this.limit = window.config.LAZY_LOAD_LIMIT;
    }
    if (window.config.USE_ALLERGEN_FILTERS && this.getDietaryInfo) {
      this.dietaryInfo = this.getDietaryInfo;
    }
    await this.fetchCategories(this.appStore.currentOrderMenuOrderType);
  },
  watch: {
    categories: {
      immediate: true,
      handler(newCategories) {
        this.flatCategories = this.flattenCategories(newCategories);
      }
    }
  },
  methods: {
    updateFoodPackagingProducts(source, preserveExisting = true) {
      const mappedFoodPackagingProducts = mapFoodPackagingProducts(source);

      if (mappedFoodPackagingProducts.length > 0 || !preserveExisting) {
        this.foodPackagingProducts = mappedFoodPackagingProducts;
      }
    },
    getFoodPackagingBasketItems(basketTile = this.basketTile) {
      const foodPackagingProductIds = new Set(
        this.foodPackagingProducts
          .map(product => Number(product?.id))
          .filter(productId => !Number.isNaN(productId))
      );

      return (basketTile?.basketItems || []).filter(item => foodPackagingProductIds.has(Number(item?.prodId)));
    },
    buildUpdatedBasketTile(result, fallbackBasketTile = this.basketTile) {
      if (!result?.basketTile) {
        return null;
      }

      return {
        ...result.basketTile,
        foodPackagingProducts: result.foodPackagingProducts || result.basketTile.foodPackagingProducts || fallbackBasketTile?.foodPackagingProducts,
      };
    },
    flattenCategories(categories) {
      const flat = [];
      categories.forEach(parent => {
        flat.push({ ...parent, isChild: false });
        if (Array.isArray(parent.children)) {
          parent.children.forEach(child => {
            flat.push({ ...child, isChild: true, parentId: parent.id });
          });
        }
      });
      return flat;
    },
    goToCheckout() {
      if (this.isFoodPackagingSelectionUpdating) {
        return;
      }

      if (!this.validateFoodPackagingSelection()) {
        return;
      }

      this.$navigateToOrderMenuCheckout();
    },
    isTakeawayOrderSelected() {
      return getCanonicalOrderType(this.appStore?.currentOrderMenuOrderType) === Order.type.TAKEAWAY;
    },
    validateFoodPackagingSelection() {
      const isFoodPackagingMandatory = this.isTakeawayOrderSelected() && this.foodPackagingProducts.length > 0;
      const hasFoodPackagingSelection = this.getFoodPackagingBasketItems().length > 0;

      this.showFoodPackagingValidationError = isFoodPackagingMandatory && !hasFoodPackagingSelection;

      return !this.showFoodPackagingValidationError;
    },
    async onCategorySelect(category) {
      this.selectedCategory = category;
      this.selectedCategoryId = category && category.id != null ? category.id : null;
      // Always fetch by category.id, as parent id already includes children products
      const products = await this.getCategoryProducts(category.id, this.appStore.currentOrderMenuOrderType);
      this.selectedCategory.products = products;
      this.basketTile = await this.getBasketTile(this.appStore.currentOrderMenuOrderType);
      this.updateFoodPackagingProducts(category);
      this.updateFoodPackagingProducts(this.basketTile);
    },
    getAcutalCategoryId() {
      if (this.selectedCategory) {
        return this.selectedCategory.id;
      }

      /*if (!this.appStore.currentOrderMenuCategoryId) {
        return null;
      }*/

      // Fallback: Select the first available category
      if (this.categories.length > 0) {
        return this.categories[0].id;
      }

      return null; // No valid category available
    },
    async fetchCategories(orderType) {
      function setParentCategoryRecursive(categories, parent = null) {
        if (!Array.isArray(categories)) return;
        categories.forEach(category => {
          if (parent) {
            category.parentCategory = parent;
          }
          if (Array.isArray(category.children) && category.children.length > 0) {
            setParentCategoryRecursive(category.children, category);
          }
        });
      }
      try {
        let categoryIdToSelect = this.getAcutalCategoryId();

        this.isLoading = true;
        const result = await productService.getCategories(orderType);
        this.categories = result.categories || [];
        this.updateFoodPackagingProducts(result, false);
        setParentCategoryRecursive(this.categories);
        console.log('Categories after mapping parentCategory:', this.categories);

        if (this.categories.length > 0) {
          if(categoryIdToSelect > 0)
          {
            const selectedCategory = this.categories.find(category => category.id === categoryIdToSelect);
            if(selectedCategory)
            {
              await this.onCategorySelect(selectedCategory)
            }
            else
            {
              await this.onCategorySelect(this.categories[0]);
            }
          }
          else
          {
            await this.onCategorySelect(this.categories[0]);
          }
        } else {
          this.selectedCategory = null; // No categories available
        }
      } catch (error) {
        console.error("Failed to fetch categories:", error);
      } finally {
        this.isLoading = false;
        this.isInitialLoad = false;
      }
    },
    async onToolbarCategoryClick(id) {
      const item = this.flatCategories.find(cat => cat.id === id);
      if (!item) return;
      this.selectedCategoryId = id;
      if (item.isChild) {
        // Fetch only child products
        this.selectedCategory = item;
        const products = await this.getCategoryProducts(item.id, this.appStore.currentOrderMenuOrderType);
        this.selectedCategory.products = products;
      } else {
        // Fetch all parent products (including children if needed)
        this.selectedCategory = item;
        const products = await this.getCategoryProducts(item.id, this.appStore.currentOrderMenuOrderType);
        this.selectedCategory.products = products;
      }
      this.basketTile = await this.getBasketTile(this.appStore.currentOrderMenuOrderType);
      this.updateFoodPackagingProducts(item);
      this.updateFoodPackagingProducts(this.basketTile);
    },
    async onChildCategorySelected(childCategory) {
      this.selectedCategory = childCategory;
      this.selectedCategoryId = childCategory && childCategory.id != null ? childCategory.id : null;
      const products = await this.getCategoryProducts(childCategory.id, this.appStore.currentOrderMenuOrderType);
      this.selectedCategory.products = products;
      this.basketTile = await this.getBasketTile(this.appStore.currentOrderMenuOrderType);
      this.updateFoodPackagingProducts(childCategory);
      this.updateFoodPackagingProducts(this.basketTile);
    },
    onBasketTileUpdated(newBasketTile) {
      this.basketTile = newBasketTile;
      this.updateFoodPackagingProducts(newBasketTile);
    },
    async handleProductQuantityChange(basketId, newQuantity) {
      try {
        let result = await basketService.editBasket(basketId, newQuantity, this.appStore.currentOrderMenuOrderType);
        if (result && result.basketTile) {
          this.updateFoodPackagingProducts(result);
          this.onBasketTileUpdated(this.buildUpdatedBasketTile(result));
        }
      } catch (e) {
        // Optionally handle error
      }
    },
    async handleFoodPackagingProductSelect(productId) {
      const normalizedProductId = Number(productId);
      if (!normalizedProductId) {
        return;
      }

      this.showFoodPackagingValidationError = false;
      this.isFoodPackagingSelectionUpdating = true;

      try {
        const existingFoodPackagingItems = this.getFoodPackagingBasketItems();
        const currentSelectedFoodPackagingItem = existingFoodPackagingItems[0] || null;
        const isSameSingleSelection =
          existingFoodPackagingItems.length === 1 &&
          Number(currentSelectedFoodPackagingItem?.prodId) === normalizedProductId &&
          Number(currentSelectedFoodPackagingItem?.quantity || 0) === 1;

        if (isSameSingleSelection) {
          return;
        }

        let latestBasketTile = this.basketTile;

        for (const existingFoodPackagingItem of existingFoodPackagingItems) {
          const removeResult = await basketService.editBasket(existingFoodPackagingItem.id, 0, this.appStore.currentOrderMenuOrderType);

          if (removeResult?.exception) {
            if (typeof this.makeToast === 'function') {
              this.makeToast('danger', this.$t('orderMenu.messages.error.removeItemFailed'));
            }
            return;
          }

          if (removeResult?.basketTile) {
            this.updateFoodPackagingProducts(removeResult);
            latestBasketTile = this.buildUpdatedBasketTile(removeResult, latestBasketTile);
            this.onBasketTileUpdated(latestBasketTile);
          }
        }

        const result = await basketService.addProductToBasket(normalizedProductId, this.appStore.currentOrderMenuOrderType, 1);

        if (result?.exception) {
          const errorCode = result.exception.code;
          const selectedFoodPackagingProduct = this.foodPackagingProducts.find(
            product => Number(product?.id) === normalizedProductId
          );
          const unavailableArticleMessage = this.$t('notification.itemUnavailable', [selectedFoodPackagingProduct?.name || this.$t('system.label.noData')]);
          const errorMessages = {
            64: 'orderMenu.messages.error.addItemMinError',
            65: 'orderMenu.messages.error.addItemMaxError',
          };

          if (typeof this.makeToast === 'function') {
            if (errorCode === 66) {
              this.makeToast('danger', unavailableArticleMessage);
            } else {
              this.makeToast('danger', this.$t(errorMessages[errorCode] || 'orderMenu.messages.error.addItemFailed'));
            }
          }
          return;
        }

        if (result?.basketTile) {
          this.updateFoodPackagingProducts(result);
          this.onBasketTileUpdated(this.buildUpdatedBasketTile(result, latestBasketTile));
        }
      } catch (error) {
        if (typeof this.makeToast === 'function') {
          this.makeToast('danger', this.$t('orderMenu.messages.error.addItemFailed'));
        }
      } finally {
        this.isFoodPackagingSelectionUpdating = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/css/customizer';

.meal-planner {
  text-align: center;
}

.meal-planner-columns {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  width: 100%;
  gap: 24px;
}

.mealplan-column {
  flex: 1 1 0;
  min-width: 0;
  width: 100%;
}

.order-menu-block {
  display: flex;
  flex-direction: column;
}

/* Remove sticky on outer columns to prevent overlap outside block */
.checkout-left,
.checkout-right {
  position: static;
  top: auto;
}

/* Replace static positioning for checkout-right with sticky */
.checkout-right {
  position: sticky;
  top: 64px;
  align-self: flex-start;
  z-index: 10;
}

/* Make checkout-left sticky as well */
.checkout-left {
  position: sticky;
  top: 24px;
  align-self: flex-start;
  z-index: 20; /* keep above right content but below headers */
}

/* Adjust inner block header/toolbar offsets to account for left column sticky top */
.order-menu-block :deep(.block-container__header),
.order-menu-block :deep(.block-container-header) {
  position: sticky;
  top: 24px; /* align with column sticky offset */
  z-index: 30;
}

.toolbar-container {
  position: sticky;
  top: calc(24px + var(--order-menu-header-height, 56px)); /* was var(--order-menu-header-height,56px) */
  z-index: 1001;
}

.fade-up-enter-active, .fade-up-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}
.fade-up-enter-from, .fade-up-leave-to {
  opacity: 0;
  transform: translateY(40px);
}
.fade-up-enter-to, .fade-up-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.basket-dialog-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.3);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 1000;
}
.basket-dialog-content {
  background: #fff;
  border-radius: 12px 12px 0 0;
  width: 100%;
  max-width: 480px;
  margin-bottom: 0;
  padding: 2rem 1.5rem 1.5rem 1.5rem;
  box-shadow: 0 -2px 16px rgba(0,0,0,0.12);
}

.basket-dialog-mobile__header {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 0.5rem;
}

.basket-dialog-mobile__close {
  flex: 0 0 auto;
}

@media (max-width: 768px) {
  .basket-dialog-overlay.basket-dialog-mobile {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0,0,0,0.3);
    display: flex;
    align-items: flex-end;
    justify-content: center;
    z-index: 1000;
    transition: none;
  }
  .basket-dialog-content.basket-dialog-content-mobile {
    width: 100vw;
    height: 100vh;
    max-width: 100vw;
    max-height: 100vh;
    border-radius: 0;
    margin: 0;
    padding: 2rem 1rem 1rem 1rem;
    box-shadow: none;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    overflow-y: auto;
  }
}

@media screen and (max-width:768px){
  .checkout-left {
    display: contents;
  }
  .checkout-right {
    display: contents;
  }
}

.sticky-toolbar {
  position: sticky;
  top: 0;
  z-index: 20;
  width: 100%;
}
.flex-layout {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  align-items: flex-start;
  gap: 24px;
  padding: 24px 0 0 0;
  width: 100%;
  box-sizing: border-box;
}
.product-section {
  flex: 2 1 0;
  min-width: 0;
}
.basket-section {
  flex: 1 1 300px;
  min-width: 300px;
  max-width: 400px;
}
@media (max-width: 768px) {
  .flex-layout {
    flex-direction: row;
    gap: 16px;
  }
  .basket-section {
    max-width: 100%;
    min-width: 0;
  }
}
</style>
